import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { ArrowRight, Buildings, User } from "@phosphor-icons/react";

const Auth = () => {
  const nav = useNavigate();
  const { login, register, loading } = useAuth();
  const [mode, setMode] = useState("login"); // login | signup
  const [role, setRole] = useState("client");
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    firm_name: "",
    company_name: "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    try {
      let u;
      if (mode === "login") {
        u = await login(form.email, form.password);
      } else {
        u = await register({ ...form, role });
      }
      toast.success(`Welcome, ${u.name}`);
      nav(u.role === "ca" ? "/dashboard" : "/portal", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#06120E] text-[#F9F6F0]">
      {/* left */}
      <div className="hidden md:block relative">
        <img
          src="https://images.unsplash.com/photo-1584940120505-117038d90b05"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#06120E] via-[#06120E]/70 to-transparent" />
        <div className="absolute bottom-16 left-16 right-16">
          <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-4">Profitink</p>
          <h2 className="font-serif-display text-4xl leading-tight">
            &ldquo;Compliance is table stakes. <br />
            <span className="italic text-[#D4AF37]">Insight is the retainer.</span>&rdquo;
          </h2>
          <p className="mt-4 text-white/60 text-sm">— The founding principle</p>
        </div>
      </div>

      {/* right */}
      <div className="flex items-center justify-center p-6 md:p-16">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-block" data-testid="auth-logo-link">
            <Logo size={36} tone="gold" wordmarkClassName="text-2xl text-[#F9F6F0]" />
          </Link>
          <h1 className="font-serif-display text-4xl mt-8 tracking-tight">
            {mode === "login" ? "Welcome back." : "Create your account."}
          </h1>
          <p className="text-white/60 text-sm mt-3">
            {mode === "login"
              ? "Sign in to access your tax command center."
              : "Choose your role and get started in seconds."}
          </p>

          {mode === "signup" && (
            <div className="mt-8 grid grid-cols-2 gap-3">
              <RoleTile
                active={role === "client"}
                onClick={() => setRole("client")}
                icon={<User size={22} weight="duotone" />}
                title="I'm a Client"
                sub="Business owner"
                testId="role-client"
              />
              <RoleTile
                active={role === "ca"}
                onClick={() => setRole("ca")}
                icon={<Buildings size={22} weight="duotone" />}
                title="I'm a CA"
                sub="Practice owner"
                testId="role-ca"
              />
            </div>
          )}

          <form onSubmit={submit} className="mt-8 space-y-4">
            {mode === "signup" && (
              <>
                <Field
                  label="Full name"
                  value={form.name}
                  onChange={set("name")}
                  required
                  testId="signup-name"
                />
                {role === "ca" ? (
                  <Field
                    label="Firm name"
                    value={form.firm_name}
                    onChange={set("firm_name")}
                    testId="signup-firm"
                  />
                ) : (
                  <Field
                    label="Company name"
                    value={form.company_name}
                    onChange={set("company_name")}
                    testId="signup-company"
                  />
                )}
              </>
            )}
            <Field
              label="Email"
              type="email"
              value={form.email}
              onChange={set("email")}
              required
              testId="auth-email"
            />
            <Field
              label="Password"
              type="password"
              value={form.password}
              onChange={set("password")}
              required
              testId="auth-password"
            />

            <button
              type="submit"
              disabled={loading}
              className="group brand-gold-bg w-full py-4 text-sm tracking-widest uppercase font-semibold flex items-center justify-center gap-3 hover:opacity-90 disabled:opacity-50"
              data-testid="auth-submit-btn"
            >
              {mode === "login" ? "Sign in" : "Create account"}
              <ArrowRight size={16} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </button>
          </form>

          <div className="mt-6 text-sm text-white/60">
            {mode === "login" ? (
              <>
                No account?{" "}
                <button className="text-[#D4AF37] hover:underline" onClick={() => setMode("signup")} data-testid="switch-signup">
                  Create one
                </button>
              </>
            ) : (
              <>
                Already have an account?{" "}
                <button className="text-[#D4AF37] hover:underline" onClick={() => setMode("login")} data-testid="switch-login">
                  Sign in
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, testId, ...rest }) => (
  <label className="block">
    <span className="text-xs tracking-widest uppercase text-white/50">{label}</span>
    <input
      {...rest}
      data-testid={testId}
      className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] outline-none py-3 text-base placeholder-white/30"
    />
  </label>
);

const RoleTile = ({ active, onClick, icon, title, sub, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`p-4 border text-left transition-colors ${
      active ? "border-[#D4AF37] bg-[#0A1B14]" : "border-white/15 hover:border-white/40"
    }`}
  >
    <div className={active ? "text-[#D4AF37]" : "text-white/70"}>{icon}</div>
    <div className="mt-2 font-serif-display text-lg">{title}</div>
    <div className="text-xs text-white/50">{sub}</div>
  </button>
);

export default Auth;
